/**
 * React Query hooks for Super Admin knowledge base CRUD.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCategories,
  getTags,
  getArticles,
  getArticleById,
  createCategory,
  updateCategory,
  deleteCategory,
  updateCategoryOrder,
  createTag,
  updateTag,
  deleteTag,
  updateTagOrder,
  createArticle,
  updateArticle,
  deleteArticle,
  updateArticleOrder,
  type CreateCategoryData,
  type UpdateCategoryData,
  type CreateTagData,
  type UpdateTagData,
  type CreateArticleData,
  type UpdateArticleData,
} from '../services/knowledgeBase';

export const kbKeys = {
  all: ['knowledgeBase'] as const,
  categories: () => [...kbKeys.all, 'categories'] as const,
  tags: () => [...kbKeys.all, 'tags'] as const,
  articles: (filters?: { categoryId?: string; status?: 'draft' | 'published'; maxResults?: number }) =>
    [...kbKeys.all, 'articles', filters ?? {}] as const,
  article: (id: string) => [...kbKeys.all, 'article', id] as const,
};

const STALE_TIME = 2 * 60 * 1000; // 2 min

// Categories
export function useKBCategories() {
  return useQuery({
    queryKey: kbKeys.categories(),
    queryFn: getCategories,
    staleTime: STALE_TIME,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCategoryData) => createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryData }) =>
      updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useUpdateCategoryOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => updateCategoryOrder(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

// Tags
export function useKBTags() {
  return useQuery({
    queryKey: kbKeys.tags(),
    queryFn: getTags,
    staleTime: STALE_TIME,
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTagData) => createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.tags() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTagData }) =>
      updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.tags() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.tags() });
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useUpdateTagOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => updateTagOrder(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.tags() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

// Articles
export function useKBArticles(options?: {
  categoryId?: string;
  status?: 'draft' | 'published';
  maxResults?: number;
}) {
  return useQuery({
    queryKey: kbKeys.articles(options),
    queryFn: () => getArticles(options),
    staleTime: STALE_TIME,
  });
}

export function useKBArticle(id: string | null) {
  return useQuery({
    queryKey: kbKeys.article(id ?? ''),
    queryFn: () => getArticleById(id!),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

export function useCreateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateArticleData) => createArticle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useUpdateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateArticleData }) =>
      updateArticle(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: kbKeys.article(variables.id) });
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}

export function useUpdateArticleOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryId,
      orderedIds,
    }: {
      categoryId: string;
      orderedIds: string[];
    }) => updateArticleOrder(categoryId, orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
      queryClient.invalidateQueries({ queryKey: kbKeys.all });
    },
  });
}
